// src/components/OrderEntry/utils/directShipAddress.js
// =============================================================================
// DIRECT SHIP PATIENT ADDRESS HELPERS
// =============================================================================

// -----------------------------------------------------------------------------
// BLOCK OEDS.UTIL.0 — Shared trim helper
// -----------------------------------------------------------------------------
const trim = (value) => String(value ?? '').trim();

// -----------------------------------------------------------------------------
// BLOCK OEDS.PARSE.0 — Parse a pasted US mailing address
// -----------------------------------------------------------------------------
// Purpose:
// - Accept the common single-line address formats used by the direct-ship dialog
// - Keep Address1 reserved for the Sage routing label "OR Residence"
// - Populate Address2/City/State/ZIP from the pasted address text
// -----------------------------------------------------------------------------
export function parseUsMailingAddress(value) {
  const raw = trim(value);
  if (!raw) {
    return { address2: '', city: '', state: '', zip: '' };
  }

  const parts = raw
    .replace(/[\r\n]+/g, ',')
    .split(',')
    .map((part) => trim(part))
    .filter(Boolean);

  if (parts.length === 0) {
    return { address2: '', city: '', state: '', zip: '' };
  }

  if (/^(USA|US|UNITED STATES(?: OF AMERICA)?)$/i.test(parts.at(-1) || '')) {
    parts.pop();
  }

  let state = '';
  let zip = '';

  const last = parts.at(-1) || '';
  const stateZipMatch = last.match(/^(.*?)(?:\s+|^)([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/);

  if (stateZipMatch) {
    const prefix = trim(stateZipMatch[1]);
    state = stateZipMatch[2].toUpperCase();
    zip = stateZipMatch[3];
    parts.pop();

    if (prefix) {
      parts.push(prefix);
    }
  } else {
    const zipOnly = last.match(/^(\d{5}(?:-\d{4})?)$/);
    const prior = parts.length > 1 ? parts.at(-2) || '' : '';
    const stateOnly = prior.match(/^([A-Za-z]{2})$/);

    if (zipOnly && stateOnly) {
      zip = zipOnly[1];
      state = stateOnly[1].toUpperCase();
      parts.pop();
      parts.pop();
    }
  }

  const city = state && zip && parts.length > 0 ? trim(parts.pop()) : '';
  const address2 = parts.join(', ');

  return { address2, city, state, zip };
}

// -----------------------------------------------------------------------------
// BLOCK OEDS.COMPLETE.0 — Validate the patient destination payload
// -----------------------------------------------------------------------------
export function isCompleteDirectShipPatient(value) {
  const patient = value && typeof value === 'object' ? value : {};

  return Boolean(
    trim(patient.shipToName) &&
    trim(patient.address1) &&
    trim(patient.address2) &&
    trim(patient.city) &&
    /^[A-Za-z]{2}$/.test(trim(patient.state)) &&
    /^\d{5}(?:-\d{4})?$/.test(trim(patient.zip))
  );
}
